import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useServices, useContractService, formatPrice } from "@/api/services";
import { getApiErrorMessage } from "@/api/client";

export default function ContractServiceModal({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const { data: servicesData } = useServices({ isActive: true, pageSize: 100 });
  const contractService = useContractService();

  const [serviceId, setServiceId] = useState("");
  const [price, setPrice] = useState("");
  const [startDate, setStartDate] = useState("");
  const [notes, setNotes] = useState("");

  const services = servicesData?.data ?? [];
  const selectedService = services.find((s) => s.id === serviceId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceId) {
      toast.error("Debe seleccionar un servicio del catálogo.");
      return;
    }
    try {
      await contractService.mutateAsync({
        clientId,
        serviceId,
        price: price || undefined,
        startDate: startDate || undefined,
        notes: notes || undefined,
      });
      toast.success("Servicio contratado correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-sm p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Contratar servicio</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Servicio del catálogo</label>
            <select className="input-field mt-1" value={serviceId} onChange={(e) => setServiceId(e.target.value)} required>
              <option value="">Seleccione un servicio...</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.defaultPrice ? `(${formatPrice(s.defaultPrice)})` : ""}
                </option>
              ))}
            </select>
            {services.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                No hay servicios activos en el catálogo. Créelos primero en la sección "Servicios".
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Precio {selectedService?.defaultPrice ? `(base: ${formatPrice(selectedService.defaultPrice)})` : ""}
            </label>
            <input
              type="number"
              min="0"
              className="input-field mt-1"
              placeholder="Dejar en blanco para usar el precio base"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Fecha de inicio</label>
            <input type="date" className="input-field mt-1" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={contractService.isPending}>
              {contractService.isPending ? "Guardando..." : "Contratar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
